import React, { createContext, useContext, useEffect, useState } from "react";

const translations = {
  en: {
    nav: { home: "Home", about: "About", projects: "Projects", experience: "Experience", education: "Education", contact: "Contact" },
    hero: {
      greeting: "Hello, I'm",
      tagline: "Full-Stack Developer building useful, scalable web products",
      intro: "I create SaaS platforms, dashboards, booking experiences, and e-commerce products from database to interface.",
      work: "View My Work",
      resume: "Download CV",
      contact: "Get In Touch",
    },
    about: {
      title: "About Me",
      subtitle: "Get to know me better",
      bio: "I'm a full-stack developer focused on building SaaS platforms, dashboards, booking experiences, and e-commerce products with React, Next.js, Node.js, and TypeScript. I enjoy turning real business problems into fast, accessible, and maintainable web applications, from database design and APIs to polished user interfaces. I'm currently open to full-time and freelance opportunities.",
      factsTitle: "Fun Facts",
      facts: ["☕ Fueled by 3+ cups of coffee daily", "💪 Gym enthusiast — never skip leg day", "♟️ Chess enthusiast, always thinking three moves ahead", "🎬 Movie buff with an ever-growing watchlist"],
      skillsTitle: "My Skills",
      categories: { Frontend: "Frontend", Backend: "Backend", Databases: "Databases", "Tools & DevOps": "Tools & DevOps" },
    },
    projects: {
      title: "My Projects",
      subtitle: "Things I've built",
      featured: "Featured",
      all: "All Projects",
      view: "View Details",
      viewLabel: "View details about",
      close: "Close project details",
      github: "GitHub",
      live: "Live Demo",
    },
    projectDescriptions: {},
    sections: {
      experience: "Experience",
      experienceSubtitle: "Where I've worked",
      education: "Education",
      educationSubtitle: "My academic journey",
    },
    contact: {
      title: "Get In Touch",
      subtitle: "Have a project in mind? Let's talk!",
      connect: "Let's Connect",
      text: "I'm always open to discussing new projects, creative ideas, or opportunities to collaborate.",
      email: "Email",
      phone: "Phone",
      whatsapp: "Chat on WhatsApp",
      whatsappMessage: "Hi Safouane, I found your portfolio and would like to talk about a project.",
      location: "Location",
      success: "Message sent successfully! I'll get back to you soon.",
      error: "Something went wrong. Please try again.",
      name: "Your Name",
      emailAddress: "Email Address",
      message: "Your Message",
      messagePlaceholder: "Tell me about your project...",
      send: "Send Message",
      sending: "Sending...",
    },
    footer: "All rights reserved.",
    language: "Language",
  },
  fr: {
    nav: { home: "Accueil", about: "À propos", projects: "Projets", experience: "Expérience", education: "Formation", contact: "Contact" },
    hero: {
      greeting: "Bonjour, je suis",
      tagline: "Développeur Full-Stack créant des produits web utiles et évolutifs",
      intro: "Je conçois des plateformes SaaS, tableaux de bord, expériences de réservation et sites e-commerce, de la base de données à l'interface.",
      work: "Voir mes projets",
      resume: "Télécharger mon CV",
      contact: "Me contacter",
    },
    about: {
      title: "À propos de moi",
      subtitle: "Apprenez à mieux me connaître",
      bio: "Je suis développeur full-stack, spécialisé dans la création de plateformes SaaS, tableaux de bord, expériences de réservation et produits e-commerce avec React, Next.js, Node.js et TypeScript. J'aime transformer des problématiques métier concrètes en applications web rapides, accessibles et maintenables, de la conception de bases de données et d'API jusqu'aux interfaces soignées. Je suis actuellement ouvert aux opportunités à temps plein et aux missions freelance.",
      factsTitle: "Quelques faits",
      facts: ["☕ Plus de trois cafés par jour", "💪 Passionné de sport — jamais d'impasse sur les jambes", "♟️ Amateur d'échecs, toujours trois coups d'avance", "🎬 Cinéphile avec une liste de films toujours plus longue"],
      skillsTitle: "Mes compétences",
      categories: { Frontend: "Frontend", Backend: "Backend", Databases: "Bases de données", "Tools & DevOps": "Outils & DevOps" },
    },
    projects: {
      title: "Mes projets",
      subtitle: "Quelques réalisations",
      featured: "À la une",
      all: "Tous les projets",
      view: "Voir les détails",
      viewLabel: "Voir les détails de",
      close: "Fermer les détails du projet",
      github: "GitHub",
      live: "Voir le site",
    },
    projectDescriptions: {
      1: "Plateforme de gestion de location de véhicules conçue pour le marché marocain : réservations, contrats PDF, notifications WhatsApp/SMS/e-mail, suivi des incidents et tableau de bord analytique en temps réel.",
      2: "Plateforme full-stack de réservation de soins de beauté marocains à Agadir, permettant de découvrir les services, choisir une spécialiste et réserver un rendez-vous dans une interface responsive et soignée.",
      3: "Plateforme intelligente de gestion de troupeaux couvrant animaux, parcelles, alimentation, stocks, santé, fournisseurs et finances, avec indicateurs, alertes et assistant IA.",
      4: "Application full-stack de gestion des finances personnelles avec authentification JWT, transactions, budgets mensuels, graphiques, export, analyses avancées et mode sombre.",
      5: "Boutique e-commerce full-stack de mode masculine premium avec catégories, recherche, panier, authentification, paiement sécurisé, mode sombre et design responsive.",
      6: "Application de gestion des études avec organisation des tâches et minuteur Pomodoro intégré pour améliorer la concentration et la productivité.",
      7: "Application ludique de minuteurs culinaires permettant de gérer plusieurs plats et de garder une organisation fluide en cuisine.",
      8: "Plateforme SaaS multi-tenant permettant aux restaurants marocains de créer des menus QR multilingues, recevoir des commandes en ligne et être notifiés via WhatsApp.",
    },
    sections: {
      experience: "Expérience",
      experienceSubtitle: "Mon parcours professionnel",
      education: "Formation",
      educationSubtitle: "Mon parcours académique",
    },
    experienceItems: {
      1: { title: "Baccalauréat", organization: "Lycée Qalb Alhikma", startDate: "Sept. 2018", endDate: "Juin 2019", description: "Études secondaires à dominante scientifique, développant de solides capacités d'analyse et de résolution de problèmes." },
      2: { title: "Études universitaires en économie", organization: "Université Ibn Zohr", startDate: "Sept. 2019", endDate: "2021", description: "Études en économie et gestion, avec une base solide en analyse, finance, statistiques et compréhension des marchés." },
      3: { title: "Diplôme en développement digital", organization: "CMC Agadir", startDate: "Sept. 2024", endDate: "Juil. 2026", description: "Spécialisation en développement web full-stack avec des projets React, Laravel, JavaScript, SQL et NoSQL, ainsi qu'une expérience pratique des workflows modernes et du versionnement." },
      4: { title: "Stagiaire développeur Full-Stack", organization: "Media Leo Tech", startDate: "Mai 2026", endDate: "Juin 2026", description: "Stage de deux mois en équipe sur un système d'acquisition de prospects qualifiés avec React, Next.js, Node.js et Express.js. Contribution également à deux sites e-commerce WordPress responsives et prêts pour la production." },
    },
    contact: {
      title: "Me contacter",
      subtitle: "Vous avez un projet en tête ? Parlons-en !",
      connect: "Échangeons",
      text: "Je suis toujours disponible pour discuter de nouveaux projets, d'idées créatives ou d'opportunités de collaboration.",
      email: "E-mail",
      phone: "Téléphone",
      whatsapp: "Discuter sur WhatsApp",
      whatsappMessage: "Bonjour Safouane, j'ai découvert votre portfolio et j'aimerais discuter d'un projet.",
      location: "Localisation",
      success: "Message envoyé ! Je vous répondrai rapidement.",
      error: "Une erreur est survenue. Veuillez réessayer.",
      name: "Votre nom",
      emailAddress: "Adresse e-mail",
      message: "Votre message",
      messagePlaceholder: "Parlez-moi de votre projet...",
      send: "Envoyer le message",
      sending: "Envoi...",
    },
    footer: "Tous droits réservés.",
    language: "Langue",
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => localStorage.getItem("portfolio-language") || "en");

  useEffect(() => {
    localStorage.setItem("portfolio-language", language);
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
